import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Search, Upload, Plus, Circle } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { partnerWorkspaceApi } from "./partnerWorkspaceApi";
import { RateCardDrawer } from "./RateCardDrawer";
import { BulkImportModal } from "./BulkImportModal";
import OnboardingRateCard from "./PartnerWorkspaceRateCardOnboarding";
import "./partnerWorkspace.css";

const GROUP_TABS = ["All", "Crew", "Equipment", "Services"];
const STATUS_PILLS = [
  { key: "all", label: "All statuses" },
  { key: "approved", label: "Approved" },
  { key: "pending", label: "Pending" },
];
const GROUP_TAG_STYLE = {
  Crew: { background: "var(--pw-tag-crew)", color: "#FFFFFF" },
  Equipment: { background: "var(--pw-tag-equipment)", color: "#FFFFFF" },
  Services: { background: "var(--pw-tag-services)", color: "#FFFFFF" },
};
// PP2c (2026-02): while there's no approved baseline yet (a Draft card being filled in, or
// already submitted and awaiting the founder+finance signoff), `header.completion` is set —
// same condition modules/partner_rate_cards.py's `_build_card_view` uses server-side. Render
// the dedicated onboarding UI instead of the TVC-style Approved-card table below, which has no
// concept of "undecided" lines and would misrender them (no proposed_rate to show).
function isOnboarding(header) {
  return !!header.completion;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function RateRow({ item, currency, onRequestChange, onViewPending }) {
  const isPending = item.status === "pending";
  return (
    <div className="grid items-center px-5 hover:bg-[color:#FCFBF6] transition-colors" data-testid={`partner-rate-card-row-${item.canonical_id}`}
      style={{ gridTemplateColumns: "2.4fr 1fr 1.3fr 1.4fr", padding: "13px 20px", borderBottom: "1px solid var(--pw-divider)" }}>
      <div className="flex items-center gap-2 min-w-0">
        {isPending
          ? <span className="h-2 w-2 rounded-full shrink-0" style={{ background: "var(--pw-yellow)" }} />
          : <Lock className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--pw-success-dot)" }} />}
        <span className="text-[14px] font-semibold truncate" style={{ color: "var(--pw-text-primary)" }}>{item.service_name}</span>
        {item.is_new && <span className="pw-pill shrink-0" style={{ background: "var(--pw-yellow)", color: "var(--pw-ink)" }}>NEW</span>}
      </div>
      <div className="text-[13px]" style={{ color: "var(--pw-text-muted)" }}>{item.unit || "—"}</div>
      <div className="text-right tabular-nums">
        {isPending ? (
          <div className="flex flex-col items-end">
            {item.rate !== null && item.rate !== undefined && (
              <span className="text-[13px] line-through" style={{ color: "var(--pw-text-faint)" }}>{formatMoney(item.rate, currency)}</span>
            )}
            <span className="text-[14px] font-bold" style={{ color: "var(--pw-pending-text)" }}>{formatMoney(item.proposed_rate, currency)}</span>
          </div>
        ) : (
          <span className="text-[14px] font-medium">{formatMoney(item.rate, currency)}</span>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        {isPending ? (
          <>
            <span className="pw-pill" style={{ background: "var(--pw-pending-bg)", color: "var(--pw-pending-text)" }}>Pending NJM Approval</span>
            <button onClick={() => onViewPending(item)} className="pw-btn-ghost px-3 py-1.5 text-[12px]" data-testid={`partner-rate-card-view-${item.canonical_id}`}>View</button>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 text-[13.5px] font-bold" style={{ color: "var(--pw-success-text)" }}><Circle className="h-2 w-2 fill-current" /> Approved</span>
            <button onClick={() => onRequestChange(item)} className="pw-btn-secondary px-3 py-1.5 text-[12px]" data-testid={`partner-rate-card-request-change-${item.canonical_id}`}>Request change</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function PartnerWorkspaceRateCard() {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("all");
  const [drawer, setDrawer] = useState(null); // { mode: "change"|"new"|"view", item }
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => {
    partnerWorkspaceApi.rateCard().then((r) => setView(r.data)).catch(() => toast.error("Couldn't load your rate card.")).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const header = view?.header || {};
  const currency = header.currency || "INR";

  const items = useMemo(() => view?.items || [], [view]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = items.filter((it) => {
      if (groupFilter !== "All" && it.group !== groupFilter) return false;
      if (statusFilter !== "all" && it.status !== statusFilter) return false;
      if (q && !(it.service_name || "").toLowerCase().includes(q) && !(it.group || "").toLowerCase().includes(q)) return false;
      return true;
    });
    const byGroup = {};
    filtered.forEach((it) => { (byGroup[it.group] = byGroup[it.group] || []).push(it); });
    return byGroup;
  }, [items, search, groupFilter, statusFilter]);

  const visibleCount = Object.values(grouped).reduce((n, arr) => n + arr.length, 0);

  const submitChange = async (canonicalId, body) => {
    await partnerWorkspaceApi.proposeChange(canonicalId, body);
    toast.success("Rate change submitted to NJM for approval");
    setDrawer(null);
    load();
  };

  const submitNew = async (body) => {
    await partnerWorkspaceApi.proposeNew(body);
    toast.success("New rate submitted — pending NJM approval");
    setDrawer(null);
    load();
  };

  if (loading) {
    return (
      <div className="pw-theme min-h-screen grid place-items-center" data-testid="partner-rate-card-loading">
        <div className="h-8 w-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "var(--pw-yellow)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (!view) {
    return (
      <div className="max-w-[1180px] mx-auto px-8 py-10" data-testid="partner-rate-card-error">
        <p style={{ color: "var(--pw-text-secondary)" }}>Your rate card isn't available right now. Please contact NJM.</p>
      </div>
    );
  }

  if (isOnboarding(header)) {
    return (
      <div className="pw-theme min-h-screen">
        <div className="max-w-[1180px] mx-auto px-8 py-10 pw-enter" data-testid="partner-rate-card-page">
          <OnboardingRateCard view={view} onReload={load} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1180px] mx-auto px-8 py-10 pw-enter" data-testid="partner-rate-card-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="pw-display text-[26px]" data-testid="partner-rate-card-title">{header.partner_name} · Production Rate Card</h1>
            <span className="pw-pill" style={{ background: "var(--pw-success-bg)", color: "var(--pw-success-text)" }} data-testid="partner-rate-card-status-pill">{(header.status || "").toUpperCase()}</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap text-[13px]" style={{ color: "var(--pw-text-muted)" }}>
            <span>Effective {fmtDate(header.effective_date)}</span>
            <span>Line items {header.line_items}</span>
            <span>Last updated {fmtDate(header.last_updated)}</span>
            {header.pending_count > 0 && <span style={{ color: "var(--pw-pending-text)" }} className="font-semibold">{header.pending_count} pending your NJM's review</span>}
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={() => setBulkOpen(true)} className="pw-btn-secondary px-4 py-2.5 text-[13px] inline-flex items-center gap-2" data-testid="partner-rate-card-bulk-import-btn">
            <Upload className="h-3.5 w-3.5" /> Bulk Import
          </button>
          <button onClick={() => setDrawer({ mode: "new" })} className="pw-btn-primary px-4 py-2.5 text-[13px] inline-flex items-center gap-2" data-testid="partner-rate-card-propose-new-btn">
            <Plus className="h-3.5 w-3.5" /> Propose New Rate
          </button>
        </div>
      </div>

      {/* Lock notice */}
      <div className="rounded-xl p-4 mb-8 flex items-start gap-3" style={{ background: "var(--pw-warm-tint)" }} data-testid="partner-rate-card-lock-notice">
        <Lock className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--pw-text-secondary)" }} />
        <p className="text-[13.5px]" style={{ color: "var(--pw-text-secondary)" }}>
          Approved rates are locked and remain operational until NJM approves a change. You can propose changes — NJM reviews and confirms before any rate becomes active.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-2 rounded-xl px-4 py-2.5 flex-1 min-w-[240px] pw-input" data-testid="partner-rate-card-search">
          <Search className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--pw-text-faint)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search rates by item or category"
            className="flex-1 bg-transparent outline-none text-[14px]" style={{ color: "var(--pw-text-primary)" }} />
        </div>
        <div className="flex items-center gap-1.5" data-testid="partner-rate-card-group-tabs">
          {GROUP_TABS.map((g) => (
            <button key={g} onClick={() => setGroupFilter(g)} data-testid={`partner-rate-card-group-tab-${g.toLowerCase()}`}
              className={g === groupFilter ? "pw-btn-primary px-3.5 py-1.5 text-[12.5px]" : "pw-btn-secondary px-3.5 py-1.5 text-[12.5px]"}>{g}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5" data-testid="partner-rate-card-status-pills">
          {STATUS_PILLS.map((s) => (
            <button key={s.key} onClick={() => setStatusFilter(s.key)} data-testid={`partner-rate-card-status-filter-${s.key}`}
              className={s.key === statusFilter ? "pw-btn-primary px-3.5 py-1.5 text-[12.5px]" : "pw-btn-secondary px-3.5 py-1.5 text-[12.5px]"}>{s.label}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="pw-card overflow-hidden" data-testid="partner-rate-card-table">
        <div className="grid items-center px-5 py-3" style={{ gridTemplateColumns: "2.4fr 1fr 1.3fr 1.4fr", background: "#FBFAF5", borderBottom: "1px solid var(--pw-divider)" }}>
          {["Item", "Unit", "Approved Rate", "Status"].map((label, i) => (
            <div key={label} className={`text-[10.5px] font-bold uppercase tracking-wider ${i >= 2 ? "text-right" : ""}`} style={{ color: "var(--pw-text-primary)" }}>{label}</div>
          ))}
        </div>
        {visibleCount === 0 ? (
          <div className="text-center py-14" data-testid="partner-rate-card-empty">
            <Search className="h-6 w-6 mx-auto mb-3" style={{ color: "var(--pw-text-faint)" }} />
            <p className="font-bold text-[14px] mb-1">No rates match your filters</p>
            <p className="text-[13px]" style={{ color: "var(--pw-text-muted)" }}>Try a different search or clear the filters.</p>
          </div>
        ) : (
          GROUP_TABS.filter((g) => g !== "All" && grouped[g]?.length).map((g) => (
            <div key={g}>
              <div className="flex items-center gap-2.5 px-5 py-2.5" style={{ background: "#FCFBF7", borderBottom: "1px solid var(--pw-divider)" }}>
                <span className="pw-pill" style={GROUP_TAG_STYLE[g]}>{g}</span>
                <span className="text-[13px]" style={{ color: "var(--pw-text-muted)" }}>{grouped[g].length} item{grouped[g].length === 1 ? "" : "s"}</span>
              </div>
              {grouped[g].map((item) => (
                <RateRow key={item.canonical_id} item={item} currency={currency}
                  onRequestChange={(it) => setDrawer({ mode: "change", item: it })}
                  onViewPending={(it) => setDrawer({ mode: "view", item: it })} />
              ))}
            </div>
          ))
        )}
        <div className="text-center py-4 text-[13px]" style={{ color: "var(--pw-text-muted)" }}>
          Showing {visibleCount} of {items.length} line items · agreed rate card
        </div>
      </div>

      {drawer && (
        <RateCardDrawer drawer={drawer} currency={currency} onClose={() => setDrawer(null)} onSubmitChange={submitChange} onSubmitNew={submitNew} />
      )}
      {bulkOpen && (
        <BulkImportModal onClose={() => setBulkOpen(false)} onImported={() => { setBulkOpen(false); load(); }} />
      )}
    </div>
  );
}
